import React from 'react'
import { isToday, min } from 'date-fns'
import { DatePicker } from './datePicker'
import { BlueInput } from './textInput'

interface TimePickerProps {
  /** The currently selected time, ex "12:00 PM" */
  time: string | undefined
  /** Callback function when the selected time changes */
  setTime?: (value: string) => void
  /** Additional class names for styling */
  className?: string
  /** Minimum time allowed */
  minTime?: string
  /** Whether the time picker is disabled, greyed out, and not clickable */
  disabled?: boolean
  /** Shorthand for the blue style variant */
  blue?: boolean
  /** Default time to show when the provided time is undefined */
  defaultTime?: string
}

/**
 * Time picker component that allows the user to select a time.
 * This component is a wrapper around the HTML input type="time" element with additional features and styling.
 */
export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  ({ time, setTime = () => {}, className, disabled, minTime, blue, defaultTime = '12:00 AM', ...props }, ref) => {
    const time24h = convertTo24HourFormat(time || defaultTime)
    const [rawInputTime, setRawInputTime] = React.useState(time24h)
    const minTime24h = minTime ? convertTo24HourFormat(minTime) : undefined

    const [warnAboutChange, setWarnAboutChange] = React.useState(false)
    const showWarn = () => {
      setWarnAboutChange(true)
      setTimeout(() => {
        setWarnAboutChange(false)
      }, 200)
    }

    const getMinutesHours = (time: string) => {
      if (!time.includes(':')) return undefined
      const [hours, minutes] = time.split(':').map((x) => parseInt(x))
      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined
      return { minutes, hours }
    }

    const lessThanMinTime = ({ hours, minutes }: { hours: number; minutes: number }) => {
      if (!minTime24h) return false
      const minTime = getMinutesHours(minTime24h)
      if (!minTime) return false
      if (hours < minTime.hours) return true
      if (hours === minTime.hours && minutes < minTime.minutes) return true
      return false
    }

    React.useEffect(() => {
      const currentTime = getMinutesHours(time24h)
      if (!currentTime) return
      if (lessThanMinTime(currentTime)) {
        setRawInputTime(minTime24h!)
        setTime(minTime24h!)
        showWarn()
      } else {
        setRawInputTime(time24h)
      }
    }, [time24h, minTime24h])

    const emitTime = () => {
      const rawTimeParsed = getMinutesHours(rawInputTime)
      if (!rawTimeParsed) {
        setRawInputTime(time24h)
        showWarn()
      } else if (lessThanMinTime(rawTimeParsed)) {
        setRawInputTime(minTime24h!)
        setTime(minTime24h!)
        showWarn()
      } else {
        setTime(rawInputTime)
      }
    }

    return (
      <input
        ref={ref}
        disabled={disabled}
        type="time"
        id="time"
        value={rawInputTime}
        onChange={(event) => setRawInputTime(event.target.value)}
        onBlur={() => emitTime()}
        onMouseLeave={() => emitTime()}
        min={minTime24h}
        className={`${blue ? BlueInput : ''} ${className} ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${warnAboutChange ? '!border !border-warning-500 !bg-warning-200' : ''} min-w-[120px] self-stretch rounded border-gray-200 placeholder-gray-400 focus:border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-50`}
        {...props}
      />
    )
  }
)

const convertTo24HourFormat = (time12h: string) => {
  const [time, modifier] = time12h.split(' ')
  let [hours, minutes, seconds] = time.split(':')
  if (hours === '12') hours = '00'
  if (modifier === 'PM') hours = (parseInt(hours, 10) + 12).toString()
  hours = hours.padStart(2, '0')
  return `${hours}:${minutes}` // ${seconds ? `:${seconds}` : ''}
}

/**
 * DateTimePicker component that combines a date picker and a time picker.
 * @param dateTime - The currently selected date and time
 * @param setDateTime - Callback function to set the selected date and time
 * @param className - Additional class names for styling
 * @param minDate - Minimum date allowed for selection
 */
export const DateTimePicker: React.FC<{
  dateTime: Date | undefined
  setDateTime: React.Dispatch<React.SetStateAction<Date | undefined>>
  className?: string
  minDate?: Date
}> = ({ dateTime, className, setDateTime, minDate }) => {
  const disabled = React.useMemo(() => (minDate ? { before: minDate } : undefined), [minDate])
  const minTime = React.useMemo(() => (dateTime && isToday(dateTime) && minDate ? minDate : undefined), [dateTime])

  return (
    <div className={`${className} flex flex-col gap-2`}>
      <DatePicker
        required
        mode="single"
        selected={dateTime}
        onSelect={wrapSetDateDay(setDateTime)}
        disabled={disabled}
      />
      <TimePicker
        disabled={dateTime === undefined}
        time={dateTime?.toLocaleTimeString()}
        minTime={minTime?.toLocaleTimeString()}
        setTime={wrapSetDateTime(setDateTime)}
        defaultTime="12:00 PM"
      />
    </div>
  )
}

export const wrapSetDateDay = (setState: React.Dispatch<React.SetStateAction<Date | undefined>>) => {
  return (newDayDate: Date) => {
    setState((prev) => {
      const newDate = new Date(prev || new Date())
      newDate.setDate(newDayDate.getDate())
      newDate.setMonth(newDayDate.getMonth())
      newDate.setFullYear(newDayDate.getFullYear())
      return newDate
    })
  }
}

export const wrapSetDateTime = (setState: React.Dispatch<React.SetStateAction<Date | undefined>>) => {
  return (time: string) => {
    setState((prev) => {
      if (!prev) return prev
      const newDate = new Date(prev)
      const [hours, minutes, seconds] = time.split(':').map((x) => parseInt(x))
      newDate.setHours(hours)
      newDate.setMinutes(minutes)
      newDate.setSeconds(seconds || 0)
      return newDate
    })
  }
}
